import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { RsIcon } from '../components/icons'
import { useAuth } from '../contexts/AuthContext'
import { homePathForRole } from '../utils/routes'
import toast from 'react-hot-toast'
import { toastEmailCodeResult } from '../utils/mailFeedback'
import { getAuthErrorMessage } from '../utils/authErrors'
import {
  AuthSplitLayout,
  AuthLogo,
} from '../components/Auth/AuthShell'
import SlideCaptcha from '../components/Auth/SlideCaptcha'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginCode, setLoginCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [codeCountdown, setCodeCountdown] = useState(0)
  const [captchaToken, setCaptchaToken] = useState('')
  const [loginCodeRequired, setLoginCodeRequired] = useState(null)
  const [credentialError, setCredentialError] = useState('')
  const [loginConfig, setLoginConfig] = useState({
    sliderCaptchaEnabled: true,
    loginVerificationCodeEnabled: true,
    masterCode8888Enabled: false,
  })
  const { login, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    axios.get('/api/auth/login-config')
      .then((res) => setLoginConfig(res.data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!authLoading && user) {
      navigate(homePathForRole(user.role), { replace: true })
    }
  }, [user, authLoading, navigate])

  useEffect(() => {
    if (codeCountdown <= 0) return undefined
    const t = window.setTimeout(() => setCodeCountdown((c) => c - 1), 1000)
    return () => window.clearTimeout(t)
  }, [codeCountdown])

  const sliderRequired = loginConfig.sliderCaptchaEnabled
  const codeFeatureEnabled = loginConfig.loginVerificationCodeEnabled
  const showCodeField = codeFeatureEnabled && loginCodeRequired !== false
  const canPreview = email.trim() && password && (!sliderRequired || captchaToken)
  const canSendCode = canPreview && loginCodeRequired !== false

  useEffect(() => {
    if (!codeFeatureEnabled) {
      setLoginCodeRequired(false)
      return undefined
    }
    if (!canPreview) {
      setLoginCodeRequired(null)
      setCredentialError('')
      return undefined
    }
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const res = await axios.post('/api/auth/login/preview', {
          email: email.trim(),
          password,
          captchaToken: sliderRequired ? captchaToken : undefined,
        })
        if (!cancelled) {
          setLoginCodeRequired(!!res.data.loginCodeRequired)
          setCredentialError('')
        }
      } catch (err) {
        if (!cancelled) {
          setLoginCodeRequired(null)
          const status = err.response?.status
          if (status === 401 || status === 403 || status === 423 || status === 400) {
            setCredentialError(getAuthErrorMessage(err, 'preview', ''))
          } else {
            setCredentialError('')
          }
        }
      }
    }, 400)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [email, password, captchaToken, sliderRequired, codeFeatureEnabled, canPreview])

  const handleSendCode = async () => {
    if (!canSendCode) {
      if (sliderRequired && !captchaToken) toast.error('Complete the slider verification first')
      return
    }
    setSendingCode(true)
    try {
      const res = await axios.post('/api/auth/login/send-code', {
        email: email.trim(),
        password,
        captchaToken: sliderRequired ? captchaToken : undefined,
      })
      if (res.data.skipped) {
        setLoginCodeRequired(false)
        toast.success(res.data.message)
      } else {
        toastEmailCodeResult(res, toast, 'Login verification code sent to your email')
        setCodeCountdown(60)
      }
    } catch (err) {
      const message = getAuthErrorMessage(err, 'send-code', 'Could not send login verification code.')
      toast.error(message)
      if (err.response?.status === 401 || err.response?.status === 403 || err.response?.status === 423) {
        setCredentialError(message)
      }
    } finally {
      setSendingCode(false)
    }
  }

  const handleCaptchaVerified = useCallback((token) => {
    setCaptchaToken(token)
  }, [])

  const handleCaptchaReset = useCallback(() => {
    setCaptchaToken('')
    setLoginCodeRequired(null)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (sliderRequired && !captchaToken) {
      toast.error('Please complete the slider verification')
      return
    }
    if (showCodeField && !loginCode.trim()) {
      toast.error('Please enter the 4-digit login verification code')
      return
    }
    setLoading(true)
    try {
      const loggedIn = await login(email.trim(), password, {
        loginCode: showCodeField ? loginCode.trim() : undefined,
        captchaToken: sliderRequired ? captchaToken : undefined,
      })
      toast.success(`Welcome back, ${loggedIn.system_name}!`)
      navigate(homePathForRole(loggedIn.role))
    } catch (err) {
      const message = getAuthErrorMessage(err, 'login', 'Login failed. Please try again.')
      toast.error(message)
      setCredentialError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthSplitLayout
      title={
        <>
          Connect Global Capital
          <br />
          with China Assets
        </>
      }
      description="RotationStreet is the trusted AI platform bridging Chinese fund managers with global investors. Secure custody, multilingual support, end-to-end deal flow."
      footer={
        <div className="auth-footer-link">
          Don&apos;t have an account? <Link to="/signup">Sign up for free →</Link>
        </div>
      }
    >
      <AuthLogo />
      <div className="auth-subtitle">Welcome back. Please sign in to continue.</div>

      <form onSubmit={handleSubmit}>
        <div className="auth-form-group">
          <label className="auth-label">Email Address</label>
          <input
            type="email"
            className="auth-input"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="auth-form-group">
          <label className="auth-label">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="auth-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: '#909399',
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <RsIcon name="eyeOff" size={18} /> : <RsIcon name="eye" size={18} />}
            </button>
          </div>
        </div>

        <SlideCaptcha
          enabled={sliderRequired}
          onVerified={handleCaptchaVerified}
          onReset={handleCaptchaReset}
        />

        {credentialError && (
          <p className="auth-login-code-hint" style={{ marginBottom: 16, color: '#dc2626' }}>
            {credentialError}
          </p>
        )}

        {codeFeatureEnabled && loginCodeRequired === false && (
          <p className="auth-login-code-hint" style={{ marginBottom: 16 }}>
            You signed in within the past 7 days — no email verification code is required.
          </p>
        )}

        {showCodeField && (
          <div className="auth-form-group">
            <label className="auth-label">Login Verification Code</label>
            <p className="auth-login-code-hint" style={{ marginTop: -4, marginBottom: 10, color: '#6b7280' }}>
              Your password must be correct before we can send a verification code to your email.
            </p>
            <div className="auth-login-code-row">
              <input
                type="text"
                className="auth-input"
                placeholder="4-digit code"
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                required
              />
              <button
                type="button"
                className="auth-btn-code"
                disabled={!canSendCode || sendingCode || codeCountdown > 0}
                onClick={handleSendCode}
              >
                {sendingCode ? 'Sending…' : codeCountdown > 0 ? `${codeCountdown}s` : 'Send code'}
              </button>
            </div>
            <p className="auth-login-code-hint">
              A 4-digit code will be sent to your registered work email. Valid for 5 minutes.
            </p>
          </div>
        )}

        <div className="auth-row">
          <label className="auth-checkbox">
            <input type="checkbox" /> Remember me
          </label>
          <Link to="/forgot-password" className="auth-link">
            Forgot password?
          </Link>
        </div>
        <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </AuthSplitLayout>
  )
}
